import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { actionBadgeClass } from '../../constants/actionColors';

const SUGGESTIONS = [
  'How many users do we have?',
  'Who deleted the exam?',
  'Show all admins',
  "Today's activity",
  'Recent logins',
  'Most active users',
  'How many violations?',
  'Who created users?',
  'Database summary',
  'Show all students',
  'This week activity',
  'Deleted users',
];

const LogList = ({ data }) => (
  <div className="space-y-1 mt-2 max-h-64 overflow-y-auto pr-1">
    {data.map(log => (
      <div key={log._id} className="flex items-start gap-2 py-1.5 border-b border-slate-800/60 last:border-0">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded border flex-shrink-0 mt-0.5 ${actionBadgeClass(log.action, 'dark')}`}>
          {log.action}
        </span>
        <div>
          <p className="text-slate-300 text-xs">{log.description}</p>
          <p className="text-slate-600 text-xs">{new Date(log.createdAt).toLocaleString()}</p>
        </div>
      </div>
    ))}
  </div>
);

const UserList = ({ data }) => (
  <div className="space-y-1 mt-2 max-h-48 overflow-y-auto">
    {data.map(u => (
      <div key={u._id} className="flex items-center gap-3 py-1.5 border-b border-slate-800/60 last:border-0">
        <div className="w-7 h-7 rounded-full bg-violet-700/40 flex items-center justify-center text-xs text-violet-300 font-bold flex-shrink-0">
          {(u.name || u.username || '?').charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-slate-300 text-xs font-medium">{u.name}</p>
          <p className="text-slate-600 text-xs">{u.username} · {u.email} · {u.role}</p>
        </div>
      </div>
    ))}
  </div>
);

const StatsDisplay = ({ data, details }) => (
  <div className="mt-2 space-y-1">
    {details && <p className="text-slate-400 text-xs">{details}</p>}
    {Array.isArray(data) ? (
      <div className="space-y-1">
        {data.map((item, i) => (
          <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded px-3 py-1.5">
            <span className="text-slate-400 text-xs">{item._id || item.role || item.userName || JSON.stringify(item)}</span>
            <span className="text-violet-400 text-xs font-bold">{item.count}</span>
          </div>
        ))}
      </div>
    ) : typeof data === 'object' ? (
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="bg-slate-800/50 rounded px-3 py-2">
            <p className="text-slate-500 text-xs capitalize">{k}</p>
            <p className="text-white font-bold text-sm">{typeof v === 'number' ? v : JSON.stringify(v)}</p>
          </div>
        ))}
      </div>
    ) : null}
  </div>
);

const MessageBubble = ({ msg }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] ${isUser ? 'order-first mr-0' : ''}`}>
        <div className={`rounded-2xl px-4 py-3 ${isUser
          ? 'bg-violet-700/60 text-white rounded-tr-sm'
          : 'bg-slate-800/80 border border-slate-700/50 text-slate-100 rounded-tl-sm'
        }`}>
          {msg.loading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-slate-500 text-xs">Querying database…</span>
            </div>
          ) : (
            <>
              {/* Render message text with **bold** support */}
              <p className="text-sm whitespace-pre-line">
                {msg.text?.split('**').map((part, i) =>
                  i % 2 === 1 ? <strong key={i} className="font-bold text-white">{part}</strong> : part
                )}
              </p>

              {/* Render structured response data */}
              {msg.result && (
                <>
                  {msg.result.type === 'logs' && msg.result.data?.length > 0 && (
                    <LogList data={msg.result.data} />
                  )}
                  {msg.result.type === 'users' && msg.result.data?.length > 0 && (
                    <UserList data={msg.result.data} />
                  )}
                  {msg.result.type === 'stats' && (
                    <StatsDisplay data={msg.result.data} details={msg.result.details} />
                  )}
                  {msg.result.type === 'exams' && msg.result.data?.length > 0 && (
                    <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                      {msg.result.data.map(e => (
                        <div key={e._id} className="flex items-center justify-between py-1.5 border-b border-slate-700/60 last:border-0">
                          <span className="text-slate-300 text-xs">{e.name}</span>
                          <span className="text-xs text-slate-500 border border-slate-700 px-2 py-0.5 rounded">{e.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <p className={`text-xs text-slate-700 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
          {msg.time}
        </p>
      </div>
    </div>
  );
};

const QueryChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      text: "Hello! I'm your database assistant. Ask me anything about the system — users, exams, violations, activity, or anything else. Try asking:\n• \"How many users do we have?\"\n• \"Who deleted the last exam?\"\n• \"Show all admins\"\n• \"What happened today?\"",
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;

    const userMsg = {
      id: Date.now(),
      role: 'user',
      text: text.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const loadingMsg = {
      id: Date.now() + 1,
      role: 'assistant',
      loading: true,
      time: '',
    };

    setMessages(prev => [...prev, userMsg, loadingMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await api.post('/super-admin/query', { query: text.trim() });
      const result = res.data.data;

      setMessages(prev => prev.map(m =>
        m.id === loadingMsg.id
          ? {
              ...m,
              loading: false,
              text: result.message,
              result,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            }
          : m
      ));
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === loadingMsg.id
          ? {
              ...m,
              loading: false,
              text: `Error: ${err.response?.data?.message || 'Failed to process query.'}`,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            }
          : m
      ));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 160px)' }}>
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-white">Database Query Chat</h2>
        <p className="text-slate-500 text-sm mt-1">Ask questions in plain English — get answers from the database</p>
      </div>

      <div className="flex-1 flex flex-col bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {/* Chat header */}
        <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">DB Assistant</p>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span className="text-green-400 text-xs">Connected to database</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        <div className="px-4 py-2 border-t border-slate-800/60 flex gap-2 overflow-x-auto">
          {SUGGESTIONS.slice(0, 6).map(s => (
            <button
              key={s}
              onClick={() => sendMessage(s)}
              disabled={sending}
              className="flex-shrink-0 text-xs text-slate-400 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-violet-600 hover:text-violet-300 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-slate-800 flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder="Ask a question about the database… (Enter to send)"
            rows={1}
            className="flex-1 bg-slate-800 border border-slate-700 text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-violet-500 placeholder-slate-600 resize-none"
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="w-10 h-10 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QueryChat;
